/**
 * Frontend login / register modal.
 *
 * Self-contained: keyboard-accessible (focus trap + ESC close), backdrop
 * click closes, and the underlying form rejects double-submits at three
 * layers:
 *
 *   1. Native: the submit button is disabled while a request is in flight.
 *   2. App: a `pending` flag short-circuits the submit handler so even a
 *      synthesised click() event from a keyboard-bound script cannot fire
 *      twice in the same request.
 *   3. Server: rate limit + lock-out (5 fails / 15 min) inside FrontendAjax.
 *
 * Designed to NOT depend on the host theme. We attempt a "let the theme
 * own this" handshake first (Woodmart's xtemos popup, WC's My Account page,
 * etc.) and only fall back to the bundled modal if the theme has nothing
 * to offer.
 */

import { ApiError, api } from '../api.js';
import { config, t, updateAuthState } from '../config.js';
import { byHook, hide, setText, show } from '../dom.js';

const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * After we hand the login off to the host theme's popup, this page keeps
 * its anonymous `marcConfig` (stale nonce, `isLogged: false`) unless the
 * theme does a full reload — which not every setup does (Woodmart AJAX
 * login). So while the hand-off is pending we poll a cheap endpoint:
 *
 *   - Still anonymous → `logged_in: false`, keep waiting.
 *   - User signed in via the popup → the cookie session changed, so our
 *     anonymous nonce no longer verifies → the API layer's `invalid_nonce`
 *     handler reloads the page once, which re-localizes the config for the
 *     logged-in session. (`logged_in: true` is handled too, for safety.)
 *
 * 5s interval stays well inside the `marc_account_summary` rate limit
 * (30/min); the watch self-stops after ~5 minutes.
 */
let hostPopupWatchTimer = null;
const watchHostPopupLogin = () => {
    if (hostPopupWatchTimer) return;
    let ticks = 0;
    hostPopupWatchTimer = setInterval(async () => {
        ticks += 1;
        if (ticks > 60) {
            clearInterval(hostPopupWatchTimer);
            hostPopupWatchTimer = null;
            return;
        }
        try {
            const data = await api.accountSummary();
            if (data?.logged_in) {
                clearInterval(hostPopupWatchTimer);
                hostPopupWatchTimer = null;
                window.location.reload();
            }
        } catch {
            // `invalid_nonce` (cookie session changed under us) triggers the
            // API layer's one-shot auto-reload; other errors we just retry.
        }
    }, 5000);
};

/**
 * Try to delegate to a host-theme login popup. Returns true if the theme
 * accepted the invitation. We deliberately probe a few well-known hooks so
 * that on a Woodmart site users get the *theme's* polished modal, while
 * sites without one fall back to ours.
 */
const tryHostThemePopup = () => {
    if (typeof window === 'undefined') return false;

    // Woodmart 7+ exposes its login popup via a small global API.
    if (typeof window.woodmart_login_popup?.open === 'function') {
        window.woodmart_login_popup.open();
        return true;
    }

    // Woodmart 8 ships markup with this anchor; clicking the existing
    // trigger reuses the theme's own bindings + animations.
    const wmTrigger = document.querySelector(
        '.wd-tools-account .wd-tools-icon, [data-popup-login], .login-form-popup-trigger',
    );
    if (wmTrigger instanceof HTMLElement) {
        wmTrigger.click();
        return true;
    }

    return false;
};

export const createLoginModal = (root, { toast, onAuthenticated } = {}) => {
    const modal = byHook('login-modal', root);
    if (!modal) {
        // Template was rendered without the modal — return a noop API so
        // callers still work.
        return {
            open: () => {
                if (tryHostThemePopup()) {
                    watchHostPopupLogin();
                    return true;
                }
                return false;
            },
            close() {},
        };
    }

    const dialog = byHook('login-dialog', modal);
    const closeBtn = byHook('login-close', modal);
    const switchBtn = byHook('login-switch', modal);
    const titleEl = byHook('login-title', modal);

    const loginForm = byHook('login-form', modal);
    const loginIdInput = byHook('login-input-id', modal);
    const loginPwInput = byHook('login-input-password', modal);
    const loginRemember = byHook('login-input-remember', modal);
    const loginSubmit = byHook('login-submit', modal);
    const loginError = byHook('login-error', modal);

    const registerForm = byHook('register-form', modal);
    const registerEmail = byHook('register-input-email', modal);
    const registerUsername = byHook('register-input-username', modal);
    const registerPassword = byHook('register-input-password', modal);
    const registerSubmit = byHook('register-submit', modal);
    const registerError = byHook('register-error', modal);

    let mode = 'login'; // or 'register'
    let pending = false;
    let lastFocused = null;

    const setMode = (next) => {
        mode = next;
        const isLogin = next === 'login';
        loginForm.hidden = !isLogin;
        registerForm.hidden = isLogin;
        setText(titleEl, isLogin
            ? t('authLoginTitle', 'Sign in')
            : t('authRegisterTitle', 'Create account'));
        setText(switchBtn, isLogin
            ? t('authSwitchToReg', 'No account yet? Create one')
            : t('authSwitchToLogin', 'Already have an account? Sign in'));
        switchBtn.hidden = !config.canRegister;
        clearErrors();
        // Move focus to the first input of the freshly-revealed pane on the
        // next paint — synchronous focus calls inside a hidden subtree are
        // ignored by some browsers.
        requestAnimationFrame(() => {
            const target = isLogin ? loginIdInput : registerEmail;
            target?.focus();
        });
    };

    const clearErrors = () => {
        hide(loginError);
        hide(registerError);
        setText(loginError, '');
        setText(registerError, '');
    };

    const showError = (el, message) => {
        setText(el, message);
        show(el);
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key !== 'Tab') return;

        const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE))
            .filter((el) => !el.hidden && el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const open = ({ initialMode = 'login' } = {}) => {
        // Prefer the host theme's popup when available — gives the visitor
        // a consistent UX with the rest of the site. Watch for the login
        // completing so this page's stale anonymous config gets refreshed
        // even when the theme signs in via AJAX without a reload.
        if (tryHostThemePopup()) {
            watchHostPopupLogin();
            return;
        }

        lastFocused = document.activeElement;
        modal.hidden = false;
        document.body.classList.add('marc-modal-open');
        document.addEventListener('keydown', onKeyDown);
        setMode(initialMode);
    };

    const close = () => {
        modal.hidden = true;
        document.body.classList.remove('marc-modal-open');
        document.removeEventListener('keydown', onKeyDown);
        clearErrors();
        loginForm.reset();
        registerForm.reset();
        // Only restore focus if the previous element is still attached and
        // visible. If the caller hid that element while the modal was open
        // (e.g. the login-gate disappears after a successful sign-in),
        // jumping focus there would either throw or, worse, focus a now-
        // hidden element and confuse keyboard navigation.
        if (
            lastFocused instanceof HTMLElement
            && document.contains(lastFocused)
            && !lastFocused.hidden
            && lastFocused.offsetParent !== null
        ) {
            lastFocused.focus({ preventScroll: true });
        }
        lastFocused = null;
    };

    const setBusy = (btn, busy) => {
        btn.disabled = busy;
        btn.dataset.busy = busy ? '1' : '0';
        const label = btn.querySelector('.marc__btn-label');
        const spinner = btn.querySelector('.marc__btn-spinner');
        if (label) label.hidden = busy;
        if (spinner) spinner.hidden = !busy;
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        if (pending) return;

        const login = loginIdInput.value.trim();
        const password = loginPwInput.value;
        if (login === '' || password === '') {
            showError(loginError, t('authMissing', 'Please enter your username/email and password.'));
            return;
        }

        pending = true;
        setBusy(loginSubmit, true);
        clearErrors();

        try {
            const data = await api.login({
                login,
                password,
                remember: loginRemember?.checked,
            });
            updateAuthState({
                nonce: data?.nonce,
                userId: data?.user_id,
                displayName: data?.display_name,
            });
            toast?.show(t('authLoginSuccess', 'Signed in. Carrying on with your order…'), 'success');
            close();
            onAuthenticated?.(data);
        } catch (error) {
            const message = error instanceof ApiError ? error.message : t('submitFailed', 'Something went wrong.');
            showError(loginError, message);
        } finally {
            pending = false;
            setBusy(loginSubmit, false);
        }
    };

    const handleRegister = async (event) => {
        event.preventDefault();
        if (pending) return;

        const email = registerEmail.value.trim();
        const username = registerUsername.value.trim();
        const password = registerPassword.value;

        if (email === '') {
            showError(registerError, t('authMissing', 'Please enter your email and password.'));
            return;
        }
        if (password.length < 8) {
            showError(registerError, t('authPasswordWeak', 'Password must be at least 8 characters long.'));
            return;
        }

        pending = true;
        setBusy(registerSubmit, true);
        clearErrors();

        try {
            const data = await api.register({ email, username, password });
            updateAuthState({
                nonce: data?.nonce,
                userId: data?.user_id,
                displayName: data?.display_name,
            });
            toast?.show(t('authRegisterSuccess', 'Welcome aboard! Your account is ready.'), 'success');
            close();
            onAuthenticated?.(data);
        } catch (error) {
            const message = error instanceof ApiError ? error.message : t('submitFailed', 'Something went wrong.');
            showError(registerError, message);
        } finally {
            pending = false;
            setBusy(registerSubmit, false);
        }
    };

    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
        // Backdrop click only — clicks inside the dialog should not close.
        if (event.target === modal) close();
    });
    switchBtn.addEventListener('click', () => {
        setMode(mode === 'login' ? 'register' : 'login');
    });
    loginForm.addEventListener('submit', handleLogin);
    registerForm.addEventListener('submit', handleRegister);

    return { open, close };
};
